import React, { useEffect, useRef, useState } from "react";

const WIN = 9999;
const LANE_X = [90, 170, 250];
const W = 340, H = 380;
const TYPES = ['sedan','sedan','sedan','sports','suv','truck','moto','moto'];
const COLS = ['#627EEA','#E8503A','#F5A623','#8B5CF6','#10B981','#EC4899','#4A90D9','#E67E22'];
const HEIGHTS = { sedan:44, sports:36, suv:52, truck:76, moto:42 };
const STARS = Array.from({length:60},()=>({
  x:Math.random()*W, y:Math.random()*H,
  r:0.5+Math.random()*1.2, twinkle:Math.random()*Math.PI*2
}));

function nightF(score){if(score<5000)return 0;return Math.min(1,(score-5000)/4000);}
function sfact(speed){return Math.min(1,(speed-2.2)/5);}
function lerp(a,b,t){return Math.round(a+(b-a)*t);}
function nightCol(hex,nf){const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);const f=1-nf*0.55;return`rgb(${Math.floor(r*f)},${Math.floor(g*f)},${Math.floor(b*f)})`;}

// ── Leaderboard component ──────────────────────────────────────────────────
function Leaderboard() {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://app02.azurewebsites.net/api/get-scores')
      .then(r => r.json())
      .then(d => { if(Array.isArray(d)) setScores(d.slice(0,10)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{textAlign:'center',padding:'12px 0',fontSize:10,color:'#8A7055',letterSpacing:'0.1em',textTransform:'uppercase'}}>
      Loading scores…
    </div>
  );
  if (!scores.length) return (
    <div style={{textAlign:'center',padding:'12px 0',fontSize:10,color:'rgba(92,74,42,0.4)',letterSpacing:'0.1em',textTransform:'uppercase'}}>
      No scores yet — be the first!
    </div>
  );

  return (
    <div style={{marginTop:8,borderTop:'1px solid rgba(201,168,76,0.15)',paddingTop:10}}>
      <div style={{fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(92,74,42,0.4)',textAlign:'center',marginBottom:8}}>
        Top 10
      </div>
      {scores.map((s,i) => (
        <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 8px',borderRadius:2,background: i===0?'rgba(201,168,76,0.08)':'transparent'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:10,fontWeight:500,color:i===0?'#C9A84C':i===1?'#8A7055':i===2?'#A0856A':'rgba(92,74,42,0.35)',minWidth:16,textAlign:'right'}}>
              {i===0?'🥇':i===1?'🥈':i===2?'🥉':`${i+1}.`}
            </span>
            <span style={{fontSize:11,color:'#2E2418',fontWeight: i<3?500:400}}>{s.name}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            {s.won && <span style={{fontSize:9,color:'#C9A84C'}}>🏆</span>}
            <span style={{fontSize:11,fontWeight:500,color:'#C9A84C',fontFamily:"'Cormorant Garamond',Georgia,serif"}}>{s.score.toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TeslaGame({ title = "My next big project", desc = "A Tesla. Yes, really. Every donation brings me one small step closer to driving in complete, electric, guilt-free silence." }) {
  const [gameActive, setGameActive] = useState(false);
  const [phase, setPhase]           = useState('idle'); // idle | playing | over | win
  const [scoreDisplay, setScoreDisplay] = useState(0);
  const [livesDisplay, setLivesDisplay] = useState(3);
  const [spdDisplay, setSpdDisplay]     = useState('1x');
  const [playerName, setPlayerName]     = useState('');
  const [saved, setSaved]               = useState(false);

  const canvasRef  = useRef(null);
  const cfcRef     = useRef(null);
  const stateRef   = useRef(null);
  const rafRef     = useRef(null);

  function initState() {
    return {
      running: false, won: false, score: 0, lives: 3,
      playerLane: 1, playerX: LANE_X[1], targetX: LANE_X[1],
      roadOff: 0, enemies: [], particles: [], confetti: [],
      lastTime: 0, spawnTimer: 0, speed: 2.2, flash: 0,
      lastLaneMove: 0, speedPhase: 0, tick: 0, lastHud: 0,
      kL: false, kR: false,
    };
  }

  // Stop the animation if the component disappears (e.g. page change)
  useEffect(() => () => {
    if (stateRef.current) stateRef.current.running = false;
    cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!stateRef.current) return;
      if (e.type === 'keydown') {
        if (e.key === 'ArrowLeft')  { stateRef.current.kL = true; e.preventDefault(); }
        if (e.key === 'ArrowRight') { stateRef.current.kR = true; e.preventDefault(); }
      } else {
        if (e.key === 'ArrowLeft')  stateRef.current.kL = false;
        if (e.key === 'ArrowRight') stateRef.current.kR = false;
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup',   onKey);
    return () => { window.removeEventListener('keydown',onKey); window.removeEventListener('keyup',onKey); };
  }, []);

  function startGame() {
    const st = initState();
    st.running = true;
    stateRef.current = st;
    setSaved(false); setPlayerName('');
    setScoreDisplay(0); setLivesDisplay(3); setSpdDisplay('1x');
    setPhase('playing');
    st.lastTime = performance.now();
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(ts => loop(ts, st));
  }

  function loop(ts, st) {
    if (!st.running) return;
    const canvas = canvasRef.current;
    const cfc    = cfcRef.current;
    if (!canvas || !cfc) return;
    const ctx  = canvas.getContext('2d');
    const cctx = cfc.getContext('2d');
    // Same game speed on 60 Hz, 120 Hz and 144 Hz screens; capped after a tab switch
    const dt   = Math.min(ts - st.lastTime, 50); st.lastTime = ts;
    const f    = dt / (1000 / 60);

    // Input
    if (ts - st.lastLaneMove > 200) {
      if (st.kL && st.playerLane > 0) { st.playerLane--; st.targetX = LANE_X[st.playerLane]; st.lastLaneMove = ts; }
      if (st.kR && st.playerLane < 2) { st.playerLane++; st.targetX = LANE_X[st.playerLane]; st.lastLaneMove = ts; }
    }
    st.playerX += (st.targetX - st.playerX) * (1 - Math.pow(0.8, f));
    st.roadOff += st.speed * f; st.speedPhase += st.speed * 0.04 * f; st.tick++;
    st.speed = 2.2 + Math.floor(st.score/250) * 0.18;
    st.score += dt * 0.025;
    if (st.flash > 0) st.flash = Math.max(0, st.flash - f);
    if (st.score >= WIN) { st.score = WIN; endGame(st, true, ctx, cctx); return; }

    // Spawn
    st.spawnTimer += dt;
    const iv = Math.max(800, 2400 - Math.floor(st.score/120)*45);
    if (st.spawnTimer > iv) { st.spawnTimer = 0; spawn(st); }
    st.enemies.forEach(e => { e.y += e.spd * f; });
    st.enemies = st.enemies.filter(e => e.y < H + 90);
    spawnP(st); st.particles = st.particles.filter(p => p.life > 0);
    checkHit(st);
    if (!st.running) return; // game ended on this frame

    // HUD — refreshed 10×/s instead of every frame (fewer React re-renders)
    if (ts - st.lastHud > 100) {
      st.lastHud = ts;
      setScoreDisplay(Math.min(Math.floor(st.score), WIN));
      setSpdDisplay(Math.max(1, Math.floor((st.speed-2.2)/0.5)+1) + 'x');
    }

    // Draw
    const nf = nightF(st.score);
    ctx.clearRect(0,0,W,H);
    drawRoad(ctx, st.roadOff, st.tick, nf);
    st.enemies.forEach(e => drawVehicle(ctx, e, nf));
    drawParticles(ctx, st.particles, st.speed, f);
    drawTesla(ctx, st.playerX, H-80, st.flash > 0, st.flash, st.speed, st.speedPhase, nf);

    rafRef.current = requestAnimationFrame(ts2 => loop(ts2, st));
  }

  function endGame(st, win, ctx, cctx) {
    st.running = false;
    if (win) { spawnConfettiArr(st); animateConfetti(st, cctx); }
    else { cctx && cctx.clearRect(0,0,W,H); }
    setPhase(win ? 'win' : 'over');
    setScoreDisplay(Math.min(Math.floor(st.score), WIN));
  }

  function checkHit(st) {
    if (st.flash > 0) return;
    for (const e of st.enemies) {
      const dx=Math.abs(e.x-st.playerX), dy=Math.abs(e.y-(H-80));
      const hw=e.type==='moto'?7:e.type==='truck'?15:e.type==='suv'?14:12;
      const hh=e.type==='moto'?20:e.type==='truck'?40:e.type==='sports'?20:26;
      if (dx<hw && dy<hh) {
        st.lives--; st.flash=70;
        setLivesDisplay(st.lives);
        if (st.lives <= 0) {
          const canvas=canvasRef.current, cfc=cfcRef.current;
          endGame(st, false, canvas?.getContext('2d'), cfc?.getContext('2d'));
        }
        return;
      }
    }
  }

  function spawn(st) {
    const type=TYPES[Math.floor(Math.random()*TYPES.length)];
    const h=HEIGHTS[type]||44;
    st.enemies.push({x:LANE_X[Math.floor(Math.random()*3)],y:-h/2-10,type,col:COLS[Math.floor(Math.random()*COLS.length)],spd:st.speed*(0.45+Math.random()*.45),h});
  }

  function spawnP(st) {
    const s=sfact(st.speed);
    if(s<0.12||Math.random()>s*0.8)return;
    const side=Math.random()<.5?-1:1;
    st.particles.push({x:st.playerX+side*(18+Math.random()*10),y:H-80+Math.random()*16-8,vx:side*(0.8+Math.random()*1.5)*s,vy:st.speed*0.45,life:1,len:4+s*18});
  }

  function spawnConfettiArr(st) {
    for(let i=0;i<70;i++) st.confetti.push({x:W*.25+Math.random()*W*.5,y:H*.15+Math.random()*H*.4,vx:(Math.random()-.5)*4,vy:-(2+Math.random()*4),col:['#C9A84C','#E8C97A','#F9F1DC','#1A1410','#8A7055'][Math.floor(Math.random()*5)],size:3+Math.random()*5,rot:Math.random()*Math.PI*2,vrot:(Math.random()-.5)*.15,life:1});
  }

  function animateConfetti(st, cctx) {
    if(!cctx) return;
    cctx.clearRect(0,0,W,H);
    for(const c of st.confetti){cctx.save();cctx.translate(c.x,c.y);cctx.rotate(c.rot);cctx.fillStyle=c.col;cctx.globalAlpha=c.life;cctx.fillRect(-c.size/2,-c.size/4,c.size,c.size/2);cctx.restore();c.x+=c.vx;c.y+=c.vy;c.vy+=0.1;c.rot+=c.vrot;c.life-=0.008;}
    st.confetti=st.confetti.filter(c=>c.life>0);
    if(st.confetti.length) requestAnimationFrame(()=>animateConfetti(st,cctx));
    else cctx.clearRect(0,0,W,H);
  }

  async function handleSave() {
    if (saved || !playerName.trim()) return;
    // Confirm at once (and only once): the server can take a few seconds to wake up,
    // and players used to click again meanwhile, saving the same score twice.
    setSaved(true);
    try {
      await fetch('https://app02.azurewebsites.net/api/save-score', {
        method: 'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ name: playerName.trim(), score: Math.floor(stateRef.current?.score||scoreDisplay), won: phase==='win' })
      });
    } catch(e) {
      console.log('save-score:', e);
    }
  }

  // Touch — useRef so value persists across renders
  const touchXRef = useRef(0);
  const onTouchStart = (e) => { touchXRef.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (!stateRef.current) return;
    const st  = stateRef.current;
    const dx  = e.changedTouches[0].clientX - touchXRef.current;
    if (Math.abs(dx) > 20) {
      const ts = performance.now();
      if (dx < 0 && st.playerLane > 0) { st.playerLane--; st.targetX = LANE_X[st.playerLane]; st.lastLaneMove = ts; }
      if (dx > 0 && st.playerLane < 2) { st.playerLane++; st.targetX = LANE_X[st.playerLane]; st.lastLaneMove = ts; }
    }
  };

  // ── UI ──────────────────────────────────────────────────────────────────────
  const gameStyle = { position:'relative', background:'#FAF6EE', border:'1px solid rgba(201,168,76,0.2)', borderRadius:4, overflow:'hidden' };

  if (!gameActive) return (
    <div>
      {/* Card — identical style to original transparency section cards */}
      <div
        onClick={()=>setGameActive(true)}
        onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 12px 40px rgba(42,28,8,0.08)';}}
        onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}
        style={{background:'#FFFFFF',borderRadius:4,padding:'24px 20px',textAlign:'center',border:'1px solid rgba(201,168,76,0.12)',cursor:'pointer',transition:'transform 0.3s, box-shadow 0.3s'}}
      >
        {/* Animated SVG — original car with speed lines from App.css */}
        <div style={{marginBottom:16}}>
          <svg viewBox="0 0 64 56" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:56,height:56,margin:'0 auto',display:'block'}}>
            {/* Speed lines */}
            <line className="speed speed1" x1="2" y1="28" x2="12" y2="28" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
            <line className="speed speed2" x1="2" y1="33" x2="9"  y2="33" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
            <line className="speed speed3" x1="2" y1="23" x2="8"  y2="23" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" opacity="0.3"/>
            {/* Car body */}
            <rect x="14" y="30" width="44" height="14" rx="3" stroke="#C9A84C" strokeWidth="1.5"/>
            <path d="M18 30v-2a18 18 0 0134 0v2" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="22" cy="38" r="3" stroke="#C9A84C" strokeWidth="1.5"/>
            <circle cx="48" cy="38" r="3" stroke="#C9A84C" strokeWidth="1.5"/>
            <path d="M26 38h18" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M54 38h2a2 2 0 002-2v-1a2 2 0 00-2-2h-2" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
        <div dir="auto" style={{fontFamily:"'Cormorant Garamond','Amiri',Georgia,serif",fontSize:20,fontWeight:400,color:'#1A1410',marginBottom:8}}>{title}</div>
        <div dir="auto" style={{fontSize:13,color:'#8A7055',lineHeight:1.7,maxWidth:240,margin:'0 auto 14px'}}>{desc}</div>
        <div style={{fontSize:10,color:'#C9A84C',letterSpacing:'0.12em',textTransform:'uppercase'}}>Click to play ↑</div>
      </div>
      <Leaderboard />
    </div>
  );

  return (
    <div style={gameStyle}>
      {/* HUD */}
      <div style={{position:'absolute',top:8,left:10,color:'#5C4A2A',fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',zIndex:10}}>
        Lives<span style={{fontSize:14,display:'block',letterSpacing:'0.15em',color:'#C9A84C'}}>{'♥ '.repeat(Math.max(0,livesDisplay)).trim()||'☆'}</span>
      </div>
      <div style={{position:'absolute',top:8,left:10,marginTop:36,color:'rgba(92,74,42,0.4)',fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',zIndex:10}}>
        Speed<span style={{fontSize:11,display:'block',color:'#C9A84C',fontWeight:500}}>{spdDisplay}</span>
      </div>
      <div style={{position:'absolute',top:8,right:10,color:'#5C4A2A',fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',textAlign:'right',zIndex:10}}>
        Score<span style={{fontSize:20,fontWeight:500,display:'block',color:'#C9A84C',lineHeight:1.2}}>{scoreDisplay}</span>
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} width={W} height={H} style={{display:'block'}} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}/>
      <canvas ref={cfcRef}    width={W} height={H} style={{position:'absolute',top:0,left:0,pointerEvents:'none'}}/>

      {/* Hint bar */}
      <div style={{color:'rgba(92,74,42,0.3)',fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',textAlign:'center',padding:'5px 0 3px',background:'#FAF6EE'}}>
        ← → keys · swipe on mobile
      </div>

      {/* Overlay */}
      {phase !== 'playing' && (
        <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(250,246,238,0.96)',zIndex:20}}>
          {phase === 'win' && <div style={{fontSize:48,marginBottom:8,animation:'tPop .7s cubic-bezier(.34,1.56,.64,1) forwards'}}>🏆</div>}
          <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:22,fontWeight:400,color:'#1A1410',marginBottom:6,textAlign:'center'}}>
            {phase==='idle'?'Ready?':phase==='win'?'You made it!':'Game Over'}
          </div>
          <div style={{color:'#8A7055',fontSize:11,marginBottom:phase==='idle'?20:12,letterSpacing:'0.1em',textTransform:'uppercase',textAlign:'center'}}>
            {phase==='idle'?'Use ← → to dodge traffic':phase==='win'?'Tesla reached the destination!':'Final score'}
          </div>
          {phase !== 'idle' && <div style={{color:'#C9A84C',fontSize:32,fontWeight:500,marginBottom:12}}>{scoreDisplay}{phase==='win'?' pts!':''}</div>}

          {/* Save score */}
          {phase !== 'idle' && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,marginBottom:14,width:220}}>
              <input value={playerName} onChange={e=>setPlayerName(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter')handleSave();}}
                placeholder="Your name to save score…" maxLength={20}
                style={{background:'#F0E8D5',border:'1px solid rgba(201,168,76,.4)',borderRadius:2,color:'#1A1410',padding:'7px 12px',fontSize:13,width:'100%',outline:'none',textAlign:'center'}}/>
              {!saved
                ? <button onClick={handleSave} style={{background:'transparent',border:'1px solid rgba(201,168,76,.4)',color:'#C9A84C',padding:'5px 16px',borderRadius:2,fontSize:11,letterSpacing:'0.1em',textTransform:'uppercase',cursor:'pointer'}}>Save score</button>
                : <div style={{color:'rgba(201,168,76,.8)',fontSize:10,letterSpacing:'0.08em'}}>✓ Score saved!</div>
              }
            </div>
          )}

          <button onClick={startGame} style={{background:'#C9A84C',color:'#1A1410',border:'none',padding:'9px 24px',borderRadius:2,fontSize:11,letterSpacing:'0.12em',textTransform:'uppercase',cursor:'pointer',fontWeight:500}}>
            {phase==='idle'?'Start':'Play again'}
          </button>
          {phase==='win' && (
            <button onClick={()=>document.getElementById('donate')?.scrollIntoView({behavior:'smooth'})}
              style={{background:'transparent',border:'1px solid rgba(201,168,76,.5)',color:'#C9A84C',padding:'7px 20px',borderRadius:2,fontSize:11,letterSpacing:'0.1em',textTransform:'uppercase',cursor:'pointer',marginTop:8}}>
              🎁 Make a donation
            </button>
          )}
        </div>
      )}

      {/* Back button */}
      <button onClick={()=>{cancelAnimationFrame(rafRef.current);setGameActive(false);setPhase('idle');}}
        style={{position:'absolute',bottom:8,right:10,background:'transparent',border:'1px solid rgba(201,168,76,.3)',color:'#8A7055',padding:'4px 10px',borderRadius:2,fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',cursor:'pointer',zIndex:30}}>
        ← Back
      </button>

      <style>{`@keyframes tPop{0%{transform:scale(0);opacity:0}100%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════
// CANVAS DRAWING FUNCTIONS
// ══════════════════════════════════════════════

function drawRoad(ctx, roadOff, tick, nf) {
  let skyR,skyG,skyB;
  if(nf<0.5){const t=nf*2;skyR=lerp(237,180,t);skyG=lerp(228,110,t);skyB=lerp(208,80,t);}
  else{const t=(nf-.5)*2;skyR=lerp(180,22,t);skyG=lerp(110,26,t);skyB=lerp(80,45,t);}
  ctx.fillStyle=`rgb(${skyR},${skyG},${skyB})`;ctx.fillRect(0,0,W,H);

  let rdR,rdG,rdB;
  if(nf<0.5){const t=nf*2;rdR=lerp(213,160,t);rdG=lerp(204,140,t);rdB=lerp(186,110,t);}
  else{const t=(nf-.5)*2;rdR=lerp(160,40,t);rdG=lerp(140,42,t);rdB=lerp(110,50,t);}
  ctx.fillStyle=`rgb(${rdR},${rdG},${rdB})`;ctx.fillRect(60,0,220,H);

  if(nf>0.4){
    const sa=Math.min(1,(nf-.4)/.4);
    STARS.forEach(s=>{
      if(s.x>58&&s.x<282)return;
      const tw=0.6+0.4*Math.sin(s.twinkle+tick*.02);
      ctx.fillStyle=`rgba(255,255,220,${sa*tw*.8})`;ctx.beginPath();ctx.arc(s.x,s.y,s.r,0,Math.PI*2);ctx.fill();
    });
  }
  for(let y=-(roadOff%60);y<H;y+=60){ctx.fillStyle=`rgba(${nf>.5?'255,200,100':'201,168,76'},${.025+nf*.01})`;ctx.fillRect(60,y,220,30);}

  const da=.18+nf*.3;
  ctx.strokeStyle=`rgba(${nf>.6?'255,220,150':'92,74,42'},${da})`;ctx.lineWidth=1.5;
  ctx.setLineDash([20,15]);ctx.lineDashOffset=-(roadOff%35);
  [130,210].forEach(x=>{ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke();});
  ctx.setLineDash([]);

  if(nf>.3){
    const ea=nf*.6;
    [60,280].forEach(x=>{
      const g=ctx.createLinearGradient(x,0,x,H);g.addColorStop(0,`rgba(201,168,76,0)`);g.addColorStop(.5,`rgba(201,168,76,${ea})`);g.addColorStop(1,`rgba(201,168,76,0)`);
      ctx.strokeStyle=g;ctx.lineWidth=1.5;ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke();
    });
  } else {
    ctx.strokeStyle='rgba(201,168,76,0.5)';ctx.lineWidth=1.5;
    [60,280].forEach(x=>{ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke();});
  }
  if(nf>0){ctx.fillStyle=`rgba(15,18,35,${nf*.45})`;ctx.fillRect(0,0,W,H);}
}

function drawHLCone(ctx,x,y,nf,range=55,angle=0.5){
  if(nf<.2)return;
  const a=Math.min(1,(nf-.2)/.5)*.65;
  const g=ctx.createRadialGradient(x,y-10,2,x,y-10,range);
  g.addColorStop(0,`rgba(255,255,200,${a*.55})`);g.addColorStop(.4,`rgba(255,255,200,${a*.2})`);g.addColorStop(1,'rgba(255,255,200,0)');
  ctx.save();ctx.beginPath();ctx.moveTo(x,y-10);ctx.lineTo(x-range*Math.tan(angle),y-10-range);ctx.lineTo(x+range*Math.tan(angle),y-10-range);ctx.closePath();
  ctx.fillStyle=g;ctx.globalCompositeOperation='lighter';ctx.fill();ctx.globalCompositeOperation='source-over';ctx.restore();
}

function drawTailGlow(ctx,x,y,nf){
  if(nf<.3)return;
  const a=Math.min(1,(nf-.3)/.5)*.5;
  const g=ctx.createRadialGradient(x,y+25,2,x,y+25,30);
  g.addColorStop(0,`rgba(220,40,40,${a*.6})`);g.addColorStop(1,'rgba(220,40,40,0)');
  ctx.save();ctx.globalCompositeOperation='lighter';ctx.fillStyle=g;ctx.beginPath();ctx.arc(x,y+25,30,0,Math.PI*2);ctx.fill();ctx.globalCompositeOperation='source-over';ctx.restore();
}

function drawTesla(ctx,x,y,fl,flash,speed,speedPhase,nf){
  if(fl&&Math.floor(flash/4)%2===1)return;
  const s=sfact(speed);
  drawHLCone(ctx,x,y,nf,65,0.5);
  if(s>.08){
    for(let i=0;i<Math.floor(s*12);i++){
      const side=i%2?-1:1,oy=-18+((i*13)%50),len=s*(14+Math.sin(speedPhase+i)*8),al=s*.3*(.6+.4*Math.sin(speedPhase*2+i));
      ctx.strokeStyle=`rgba(201,168,76,${al})`;ctx.lineWidth=.7;ctx.beginPath();ctx.moveTo(x+side*17,y+oy);ctx.lineTo(x+side*(17+len),y+oy);ctx.stroke();
    }
  }
  if(s>.45){const g=ctx.createRadialGradient(x,y,6,x,y,30+s*15);g.addColorStop(0,`rgba(201,168,76,${s*.09})`);g.addColorStop(1,'rgba(0,0,0,0)');ctx.fillStyle=g;ctx.beginPath();ctx.arc(x,y,30+s*15,0,Math.PI*2);ctx.fill();}
  ctx.save();ctx.translate(x,y);ctx.scale(1,1-s*.04);
  ctx.fillStyle=`rgba(92,74,42,${.1-nf*.05})`;ctx.beginPath();ctx.ellipse(1.5,2,13,25,0,0,Math.PI*2);ctx.fill();
  const bL=Math.round(242-nf*60);
  ctx.fillStyle=`rgb(${bL},${Math.round(237-nf*55)},${Math.round(226-nf*40)})`;
  ctx.beginPath();ctx.moveTo(-4,-28);ctx.bezierCurveTo(-10,-28,-14,-23,-14,-17);ctx.lineTo(-14,-10);ctx.lineTo(14,-10);ctx.lineTo(14,-17);ctx.bezierCurveTo(14,-23,10,-28,4,-28);ctx.closePath();ctx.fill();
  const bM=Math.round(237-nf*55);
  ctx.fillStyle=`rgb(${bM},${Math.round(232-nf*50)},${Math.round(220-nf*38)})`;
  ctx.beginPath();ctx.moveTo(-14,-17);ctx.lineTo(-14,17);ctx.bezierCurveTo(-14,22,-10,24,-4,24);ctx.lineTo(4,24);ctx.bezierCurveTo(10,24,14,22,14,17);ctx.lineTo(14,-17);ctx.closePath();ctx.fill();
  const bD=Math.round(229-nf*50);
  ctx.fillStyle=`rgb(${bD},${Math.round(223-nf*45)},${Math.round(208-nf*35)})`;
  ctx.beginPath();ctx.moveTo(-14,17);ctx.bezierCurveTo(-14,22,-10,24,-4,24);ctx.lineTo(4,24);ctx.bezierCurveTo(10,24,14,22,14,17);ctx.lineTo(14,14);ctx.lineTo(-14,14);ctx.closePath();ctx.fill();
  ctx.fillStyle='rgba(16,12,10,0.88)';ctx.beginPath();ctx.roundRect(-10,-16,20,30,2);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.05)';ctx.beginPath();ctx.roundRect(-9,-15,7,28,1.5);ctx.fill();
  ctx.strokeStyle='rgba(255,255,255,0.07)';ctx.lineWidth=.8;ctx.beginPath();ctx.moveTo(-5,-14);ctx.lineTo(-5,13);ctx.stroke();
  ctx.fillStyle='rgba(26,20,16,0.65)';ctx.beginPath();ctx.roundRect(-10,-22,20,7,2);ctx.fill();
  ctx.strokeStyle='rgba(255,255,255,0.1)';ctx.lineWidth=.6;ctx.beginPath();ctx.moveTo(-6,-21);ctx.lineTo(-2,-17);ctx.stroke();ctx.beginPath();ctx.moveTo(4,-21);ctx.lineTo(0,-17);ctx.stroke();
  ctx.fillStyle='rgba(26,20,16,0.55)';ctx.beginPath();ctx.roundRect(-9,17,18,5,1.5);ctx.fill();
  [[-1],[1]].forEach(([d])=>{
    ctx.fillStyle=`rgb(${Math.round(216-nf*40)},${Math.round(208-nf*35)},${Math.round(188-nf*30)})`;ctx.fillRect(d*14,-8,d*4,5);
    ctx.fillStyle='rgba(16,12,10,0.4)';ctx.beginPath();ctx.ellipse(d*16,-5,1.5,1,0,0,Math.PI*2);ctx.fill();
  });
  const hla=.95+nf*.05;
  ctx.fillStyle=`rgba(255,255,210,${hla})`;ctx.beginPath();ctx.roundRect(-12,-28,5,3,1);ctx.fill();ctx.beginPath();ctx.roundRect(7,-28,5,3,1);ctx.fill();
  if(nf>.15){ctx.shadowColor='rgba(255,255,180,0.8)';ctx.shadowBlur=6*(nf-.15)*.8;ctx.fillStyle=`rgba(255,255,210,${hla})`;ctx.beginPath();ctx.roundRect(-12,-28,5,3,1);ctx.fill();ctx.beginPath();ctx.roundRect(7,-28,5,3,1);ctx.fill();ctx.shadowBlur=0;}
  ctx.strokeStyle=`rgba(255,255,200,${.3+nf*.3})`;ctx.lineWidth=.8;ctx.beginPath();ctx.moveTo(-7,-27);ctx.lineTo(7,-27);ctx.stroke();
  ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-12,23,24,3,1);ctx.fill();
  if(nf>.2){ctx.shadowColor='rgba(220,40,40,0.9)';ctx.shadowBlur=8*nf;ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-12,23,24,3,1);ctx.fill();ctx.shadowBlur=0;}
  ctx.fillStyle='rgba(180,160,110,0.5)';ctx.font='bold 5px sans-serif';ctx.textAlign='center';ctx.fillText('T',0,-19);
  ctx.restore();
}

function drawVehicle(ctx,e,nf){
  drawHLCone(ctx,e.x,e.y+e.h/2+4,nf,50,0.45);
  drawTailGlow(ctx,e.x,e.y-e.h/2-4,nf);
  ctx.save();ctx.translate(e.x,e.y);
  if(e.type==='truck')drawTruck(ctx,e.col,nf);
  else if(e.type==='moto')drawMoto(ctx,e.col,nf);
  else if(e.type==='suv')drawSUV(ctx,e.col,nf);
  else if(e.type==='sports')drawSports(ctx,e.col,nf);
  else drawSedan(ctx,e.col,nf);
  ctx.restore();
}

function glowHL(ctx,shapes,nf,col='255,255,180'){
  if(nf>.2){ctx.shadowColor=`rgba(${col},0.9)`;ctx.shadowBlur=5*nf;shapes();ctx.shadowBlur=0;}
}
function glowTL(ctx,shapes,nf){
  if(nf>.2){ctx.shadowColor='rgba(220,40,40,0.9)';ctx.shadowBlur=6*nf;shapes();ctx.shadowBlur=0;}
}

function drawSedan(ctx,col,nf){
  const nc=nightCol(col,nf);
  ctx.fillStyle='rgba(92,74,42,0.08)';ctx.beginPath();ctx.ellipse(1,2,11,22,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=nc;ctx.beginPath();ctx.roundRect(-11,-22,22,44,5);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.1)';ctx.beginPath();ctx.roundRect(-11,-22,22,9,5);ctx.fill();
  ctx.fillStyle='rgba(18,14,10,0.85)';ctx.beginPath();ctx.roundRect(-8,-12,16,26,2);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.05)';ctx.beginPath();ctx.roundRect(-7,-11,6,24,1.5);ctx.fill();
  ctx.fillStyle='rgba(26,20,16,0.6)';ctx.beginPath();ctx.roundRect(-8,-22,16,7,3);ctx.fill();ctx.beginPath();ctx.roundRect(-8,16,16,5,2);ctx.fill();
  const hl=()=>{ctx.fillStyle=`rgba(255,255,200,${.85+nf*.15})`;ctx.beginPath();ctx.roundRect(-9,-24,4,2.5,.8);ctx.fill();ctx.beginPath();ctx.roundRect(5,-24,4,2.5,.8);ctx.fill();};
  hl();glowHL(ctx,hl,nf);
  const tl=()=>{ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-9,21,4,2,.8);ctx.fill();ctx.beginPath();ctx.roundRect(5,21,4,2,.8);ctx.fill();};
  tl();glowTL(ctx,tl,nf);
}

function drawSports(ctx,col,nf){
  const nc=nightCol(col,nf);
  ctx.fillStyle='rgba(92,74,42,0.08)';ctx.beginPath();ctx.ellipse(1,1,13,18,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=nc;ctx.beginPath();ctx.moveTo(0,-18);ctx.bezierCurveTo(7,-18,13,-10,13,-2);ctx.bezierCurveTo(13,8,8,18,0,18);ctx.bezierCurveTo(-8,18,-13,8,-13,-2);ctx.bezierCurveTo(-13,-10,-7,-18,0,-18);ctx.closePath();ctx.fill();
  ctx.fillStyle='rgba(18,14,10,0.88)';ctx.beginPath();ctx.roundRect(-8,-10,16,22,3);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.06)';ctx.beginPath();ctx.roundRect(-7,-9,6,20,2);ctx.fill();
  ctx.fillStyle='rgba(26,20,16,0.6)';ctx.beginPath();ctx.roundRect(-10,-16,20,7,2);ctx.fill();ctx.beginPath();ctx.roundRect(-8,13,16,4,1.5);ctx.fill();
  const hl=()=>{ctx.fillStyle=`rgba(255,255,200,${.85+nf*.15})`;ctx.beginPath();ctx.roundRect(-9,-19,5,2,.8);ctx.fill();ctx.beginPath();ctx.roundRect(4,-19,5,2,.8);ctx.fill();};
  hl();glowHL(ctx,hl,nf);
  const tl=()=>{ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-9,16,18,2.5,1);ctx.fill();};
  tl();glowTL(ctx,tl,nf);
}

function drawSUV(ctx,col,nf){
  const nc=nightCol(col,nf);
  ctx.fillStyle='rgba(92,74,42,0.08)';ctx.beginPath();ctx.ellipse(1,2,13,26,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=nc;ctx.beginPath();ctx.roundRect(-13,-26,26,52,4);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.1)';ctx.beginPath();ctx.roundRect(-13,-26,26,10,4);ctx.fill();
  ctx.fillStyle='rgba(18,14,10,0.85)';ctx.beginPath();ctx.roundRect(-9,-14,18,32,2);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.05)';ctx.beginPath();ctx.roundRect(-8,-13,7,30,1.5);ctx.fill();
  ctx.strokeStyle='rgba(0,0,0,0.12)';ctx.lineWidth=.7;[-3,0,3].forEach(x=>{ctx.beginPath();ctx.moveTo(x,-12);ctx.lineTo(x,16);ctx.stroke();});
  ctx.fillStyle='rgba(26,20,16,0.6)';ctx.beginPath();ctx.roundRect(-10,-26,20,8,3);ctx.fill();ctx.beginPath();ctx.roundRect(-10,19,20,6,2);ctx.fill();
  const hl=()=>{ctx.fillStyle=`rgba(255,255,200,${.85+nf*.15})`;ctx.beginPath();ctx.roundRect(-11,-27,6,2.5,.8);ctx.fill();ctx.beginPath();ctx.roundRect(5,-27,6,2.5,.8);ctx.fill();};
  hl();glowHL(ctx,hl,nf);
  const tl=()=>{ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-11,23,6,2.5,.8);ctx.fill();ctx.beginPath();ctx.roundRect(5,23,6,2.5,.8);ctx.fill();};
  tl();glowTL(ctx,tl,nf);
}

function drawTruck(ctx,col,nf){
  const nc=nightCol(col,nf);
  ctx.fillStyle='rgba(92,74,42,0.08)';ctx.beginPath();ctx.ellipse(1,25,12,38,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=nf>.3?`rgb(${Math.round(110-nf*55)},${Math.round(100-nf*50)},${Math.round(90-nf*45)})`:'#8A8070';
  ctx.beginPath();ctx.roundRect(-14,6,28,58,2);ctx.fill();
  ctx.strokeStyle=`rgba(0,0,0,${.1+nf*.05})`;ctx.lineWidth=.7;for(let i=0;i<4;i++){ctx.beginPath();ctx.moveTo(-14,12+i*13);ctx.lineTo(14,12+i*13);ctx.stroke();}
  ctx.strokeStyle='rgba(255,255,255,0.06)';ctx.lineWidth=1;ctx.beginPath();ctx.roundRect(-14,6,28,58,2);ctx.stroke();
  ctx.fillStyle=nc;ctx.beginPath();ctx.roundRect(-12,-16,24,26,3);ctx.fill();
  ctx.fillStyle='rgba(18,14,10,0.82)';ctx.beginPath();ctx.roundRect(-9,-14,18,14,2);ctx.fill();
  ctx.fillStyle='rgba(255,255,255,0.05)';ctx.beginPath();ctx.roundRect(-8,-13,7,12,1.5);ctx.fill();
  ctx.fillStyle='rgba(26,20,16,0.6)';ctx.beginPath();ctx.roundRect(-12,-18,24,5,2);ctx.fill();
  const hl=()=>{ctx.fillStyle=`rgba(255,255,200,${.85+nf*.15})`;ctx.beginPath();ctx.roundRect(-10,-20,5,2.5,.8);ctx.fill();ctx.beginPath();ctx.roundRect(5,-20,5,2.5,.8);ctx.fill();};
  hl();glowHL(ctx,hl,nf);
  const tl=()=>{ctx.fillStyle='rgba(200,30,30,0.9)';ctx.beginPath();ctx.roundRect(-12,60,8,3,1);ctx.fill();ctx.beginPath();ctx.roundRect(4,60,8,3,1);ctx.fill();};
  tl();glowTL(ctx,tl,nf);
}

function drawMoto(ctx,col,nf){
  const nc=nightCol(col,nf);
  ctx.fillStyle='rgba(92,74,42,0.07)';ctx.beginPath();ctx.ellipse(0,0,4,16,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=`rgba(${nf>.5?'20,16,12':'30,24,18'},0.72)`;ctx.beginPath();ctx.roundRect(-4,10,8,10,3);ctx.fill();ctx.beginPath();ctx.roundRect(-3,-18,6,8,3);ctx.fill();
  ctx.strokeStyle='rgba(50,38,22,0.55)';ctx.lineWidth=1.5;ctx.lineCap='round';ctx.beginPath();ctx.moveTo(0,-10);ctx.lineTo(0,10);ctx.stroke();
  ctx.fillStyle=nc;ctx.beginPath();ctx.roundRect(-3,-8,6,12,2);ctx.fill();
  ctx.fillStyle='rgba(40,30,20,0.85)';ctx.beginPath();ctx.ellipse(0,-1,3.5,5,0,0,Math.PI*2);ctx.fill();
  ctx.fillStyle='rgba(50,40,25,0.9)';ctx.beginPath();ctx.arc(0,-4,3,0,Math.PI*2);ctx.fill();
  ctx.fillStyle=`rgba(180,160,80,${.25+nf*.1})`;ctx.beginPath();ctx.roundRect(-2,-5,4,2,1);ctx.fill();
  ctx.strokeStyle='rgba(70,55,30,0.55)';ctx.lineWidth=1.2;ctx.beginPath();ctx.moveTo(-5,-9);ctx.lineTo(5,-9);ctx.stroke();
  const hl=()=>{ctx.fillStyle=`rgba(255,255,200,${.85+nf*.15})`;ctx.beginPath();ctx.arc(0,-20,1.5,0,Math.PI*2);ctx.fill();};
  hl();glowHL(ctx,hl,nf);
  const tl=()=>{ctx.fillStyle='rgba(200,30,30,0.85)';ctx.beginPath();ctx.arc(0,18,1.2,0,Math.PI*2);ctx.fill();};
  tl();glowTL(ctx,tl,nf);
}

function drawParticles(ctx,particles,speed,f=1){
  const s=sfact(speed);
  for(const p of particles){
    ctx.strokeStyle=`rgba(201,168,76,${p.life*s*.4})`;ctx.lineWidth=.6;
    ctx.beginPath();ctx.moveTo(p.x,p.y);ctx.lineTo(p.x-p.vx*p.len*.2,p.y+p.vy*p.len*.2);ctx.stroke();
    p.x+=p.vx*f;p.y+=p.vy*f;p.life-=.05*f;
  }
}

